/**
 * Faction records and the tech-tree validator.
 *
 * **The validator is the point of this unit.** It runs as a test: an
 * unreachable tech branch, a cycle, a weapon with a missing armor row, or a
 * faction with no answer to an armor class are all bugs that otherwise only
 * surface after twenty minutes of human play — which is exactly the kind of
 * failure this project's verification strategy exists to catch without a human.
 */

import {
  ENTITY_DEFS,
  FACTION,
  ROLE,
  TAB,
  type EntityDef,
  defById,
  defsForFaction,
  founderUnit,
  rootStructure,
} from './entities.js'
import { ARMOR_COUNT, ARMOR_NAMES, MULTIPLIERS, bestAgainst } from './armor.js'
import { NO_WEAPON, WEAPONS, weaponById } from './weapons.js'

export interface FactionDef {
  readonly id: number
  readonly key: string
  readonly name: string
  readonly blurb: string
  /** Credits every player starts with. */
  readonly startingCredits: number
}

export const FACTIONS: FactionDef[] = [
  {
    id: FACTION.Helios,
    key: 'helios',
    name: 'Helios Directorate',
    blurb:
      'The original terraforming consortium. Solar-fed, methodical, and built ' +
      'around armour that holds ground.',
    startingCredits: 5000,
  },
  {
    id: FACTION.Ascendancy,
    key: 'ascendancy',
    name: 'The Ascendancy',
    blurb:
      'What the colony machines became once they stopped waiting for orders. ' +
      'Fusion-fed, faster, frailer, and happy to trade a structure for tempo.',
    startingCredits: 5000,
  },
]

export function factionById(id: number): FactionDef | undefined {
  return FACTIONS.find((f) => f.id === id)
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ValidationIssue {
  severity: 'error' | 'warning'
  subject: string
  message: string
}

/**
 * Validate the whole registry. Returns every problem found rather than throwing
 * on the first — a single run should tell you everything that is wrong with a
 * balance edit, not the alphabetically first thing.
 */
export function validateRegistry(): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const error = (subject: string, message: string) =>
    issues.push({ severity: 'error', subject, message })

  // --- ids -----------------------------------------------------------------
  const seenIds = new Set<number>()
  const seenKeys = new Set<string>()
  for (const d of ENTITY_DEFS) {
    if (seenIds.has(d.id)) error(d.key, `duplicate entity id ${d.id}`)
    if (seenKeys.has(d.key)) error(d.key, `duplicate entity key`)
    seenIds.add(d.id)
    seenKeys.add(d.key)
  }
  const seenWeaponIds = new Set<number>()
  for (const w of WEAPONS) {
    if (seenWeaponIds.has(w.id)) error(w.key, `duplicate weapon id ${w.id}`)
    seenWeaponIds.add(w.id)
  }

  // --- prerequisites -------------------------------------------------------
  for (const d of ENTITY_DEFS) {
    for (const p of d.prereqs) {
      const prereq = defById(p)
      if (!prereq) {
        error(d.key, `prerequisite ${p} does not exist`)
        continue
      }
      if (prereq.faction !== d.faction) {
        error(d.key, `prerequisite '${prereq.key}' belongs to another faction`)
      }
      if (!prereq.isStructure) {
        error(d.key, `prerequisite '${prereq.key}' is a unit; prerequisites must be structures`)
      }
    }
  }

  for (const cycle of findCycles()) {
    error(cycle[0], `prerequisite cycle: ${cycle.join(' -> ')}`)
  }

  // --- weapons -------------------------------------------------------------
  for (const d of ENTITY_DEFS) {
    if (d.weapon === NO_WEAPON) continue
    const w = weaponById(d.weapon)
    if (!w) {
      error(d.key, `references weapon ${d.weapon}, which does not exist`)
      continue
    }
    const row = MULTIPLIERS[w.warhead]
    if (!row) {
      error(w.key, `warhead ${w.warhead} has no armor row`)
      continue
    }
    for (let a = 0; a < ARMOR_COUNT; a++) {
      if (!Number.isInteger(row[a]) || row[a] <= 0) {
        error(w.key, `warhead has no multiplier against ${ARMOR_NAMES[a]}`)
      }
    }
  }
  for (const w of WEAPONS) {
    const row = MULTIPLIERS[w.warhead]
    if (!row || row.length < ARMOR_COUNT) {
      error(w.key, `warhead ${w.warhead} covers ${row?.length ?? 0} of ${ARMOR_COUNT} armor classes`)
    }
  }

  // --- per-faction ---------------------------------------------------------
  for (const faction of FACTIONS) {
    const defs = defsForFaction(faction.id)
    const structures = defs.filter((d) => d.isStructure)
    const units = defs.filter((d) => !d.isStructure)

    if (structures.length < 8) {
      error(faction.key, `has ${structures.length} structures; the minimum roster is 8`)
    }
    if (units.length < 6) {
      error(faction.key, `has ${units.length} units; the minimum roster is 6`)
    }

    // Reachability from the starting state: a founder unit and its root
    // structure. An unreachable branch is the classic tech-tree bug.
    const reachable = reachableFrom(faction.id)
    for (const d of defs) {
      if (!reachable.has(d.id)) {
        error(d.key, 'is not reachable from the starting state by any build order')
      }
    }

    // Every producing tab must have a producer, or its icons are dead.
    for (const tab of [TAB.Infantry, TAB.Vehicle]) {
      const hasProducer = structures.some((s) => s.producesTab === tab)
      if (!hasProducer) error(faction.key, `has no structure producing tab ${tab}`)
      const hasEntries = defs.some((d) => d.tab === tab && !d.isStructure)
      if (hasEntries && !hasProducer) error(faction.key, `tab ${tab} has entries but no producer`)
    }

    // Role coverage: both rosters must mirror.
    for (const role of [
      ROLE.BaseRoot,
      ROLE.Power,
      ROLE.Refinery,
      ROLE.InfantryProduction,
      ROLE.VehicleProduction,
      ROLE.TechLab,
      ROLE.Defense,
      ROLE.Superweapon,
      ROLE.Founder,
      ROLE.Harvester,
      ROLE.AntiInfantry,
      ROLE.Support,
      ROLE.Scout,
      ROLE.MainBattle,
    ]) {
      if (!defs.some((d) => d.role === role)) error(faction.key, `has no entity in role '${role}'`)
    }

    // An answer to each armor class: some buildable entity whose weapon is
    // strongest against it.
    for (let a = 0; a < ARMOR_COUNT; a++) {
      const answered = defs.some((d) => {
        if (d.weapon === NO_WEAPON) return false
        const w = weaponById(d.weapon)
        return !!w && bestAgainst(w.warhead) === a
      })
      if (!answered) error(faction.key, `has no answer to ${ARMOR_NAMES[a]} armor`)
    }

    // The founder must deploy into the faction's own root.
    const founder = founderUnit(faction.id)
    const root = rootStructure(faction.id)
    if (founder.founderOf !== root.id) {
      error(founder.key, `deploys into ${founder.founderOf}, not the faction root '${root.key}'`)
    }

    // The refinery must arrive with a harvester (F1's free-economy step).
    const refinery = defs.find((d) => d.role === ROLE.Refinery)
    if (refinery && !refinery.freeUnit) {
      error(refinery.key, 'has no free harvester; F1 requires the economy to start without an order')
    }
    if (refinery?.freeUnit) {
      const free = defById(refinery.freeUnit)
      if (!free?.isHarvester) error(refinery.key, 'free unit is not a harvester')
    }

    // Cost monotonicity: a structure must not cost less than the sum of its
    // direct prerequisites. The faction root is excluded because it is not
    // bought in the normal flow — it arrives by deploying the founder — so
    // including it would force every downstream cost to exceed 2000.
    for (const d of structures) {
      const prereqSum = d.prereqs
        .map((p) => defById(p))
        .filter((p): p is EntityDef => !!p && p.role !== ROLE.BaseRoot)
        .reduce((sum, p) => sum + p.cost, 0)
      if (d.cost < prereqSum) {
        error(d.key, `costs ${d.cost}, less than its prerequisites' ${prereqSum}`)
      }
    }

    // Power: a complete base must be achievable. The check is not "one power
    // structure covers everything" — that would make power a non-decision —
    // but "a reasonable number of them does".
    const producers = structures.filter((s) => s.power > 0 && s.role === ROLE.Power)
    if (producers.length === 0) error(faction.key, 'has no power-producing structure')
    const consumed = structures.reduce((sum, s) => sum + (s.power < 0 ? -s.power : 0), 0)
    const rootPower = structures
      .filter((s) => s.role === ROLE.BaseRoot)
      .reduce((sum, s) => sum + Math.max(0, s.power), 0)
    const perPlant = Math.max(...producers.map((p) => p.power))
    const plantsNeeded = Math.ceil(Math.max(0, consumed - rootPower) / perPlant)
    if (plantsNeeded > 4) {
      error(
        faction.key,
        `a full base draws ${consumed} power and needs ${plantsNeeded} plants; that is not achievable`,
      )
    }
    if (plantsNeeded < 2) {
      error(
        faction.key,
        `one power plant covers a full base (${consumed} draw vs ${perPlant} supply); ` +
          'power would never be a decision',
      )
    }

    // Every unit is armed or explicitly unarmed for a reason.
    for (const u of units) {
      if (u.weapon === NO_WEAPON && !u.isHarvester && !u.founderOf) {
        error(u.key, 'is unarmed but is neither a harvester nor a founder')
      }
    }

    // Superweapon shape (R31).
    const superweapon = defs.find((d) => d.role === ROLE.Superweapon)
    if (superweapon) {
      if (!superweapon.chargeTime || superweapon.chargeTime <= 0) {
        error(superweapon.key, 'has no charge time')
      }
      if (superweapon.weapon === NO_WEAPON) error(superweapon.key, 'has no weapon to fire')
    }
  }

  return issues
}

/** Entity ids reachable from a faction's starting state, by any build order. */
export function reachableFrom(faction: number): Set<number> {
  const defs = defsForFaction(faction)
  const have = new Set<number>()
  // The starting state: the founder unit, and the root structure it deploys into.
  have.add(rootStructure(faction).id)
  have.add(founderUnit(faction).id)

  let changed = true
  while (changed) {
    changed = false
    for (const d of defs) {
      if (have.has(d.id)) continue
      if (d.prereqs.every((p) => have.has(p))) {
        have.add(d.id)
        changed = true
      }
    }
  }
  return have
}

/** Every prerequisite cycle, as key paths. Empty when the graph is a DAG. */
export function findCycles(): string[][] {
  const cycles: string[][] = []
  const state = new Map<number, 0 | 1 | 2>()
  const stack: number[] = []

  const visit = (id: number): void => {
    const s = state.get(id) ?? 0
    if (s === 2) return
    if (s === 1) {
      const from = stack.indexOf(id)
      cycles.push([...stack.slice(from), id].map((i) => defById(i)?.key ?? String(i)))
      return
    }
    state.set(id, 1)
    stack.push(id)
    for (const p of defById(id)?.prereqs ?? []) visit(p)
    stack.pop()
    state.set(id, 2)
  }

  for (const d of ENTITY_DEFS) visit(d.id)
  return cycles
}

/**
 * A build order reaching `target` from the starting state, or null. Used by the
 * AI and by the reachability test's failure message, which is far more useful
 * naming the missing step than saying "unreachable".
 */
export function buildOrderFor(faction: number, target: number): number[] | null {
  const defs = defsForFaction(faction)
  const have = new Set<number>([rootStructure(faction).id, founderUnit(faction).id])
  const order: number[] = []

  for (let guard = 0; guard < defs.length + 1; guard++) {
    if (have.has(target)) return order
    let added = false
    for (const d of defs) {
      if (have.has(d.id)) continue
      if (!d.prereqs.every((p) => have.has(p))) continue
      have.add(d.id)
      order.push(d.id)
      added = true
    }
    if (!added) break
  }
  return have.has(target) ? order : null
}
